import { useSetAtom } from "jotai";
import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import {
  Calendar,
  GlobeCrossedOut,
  InfoCircle,
  Refresh,
  RefreshCrossedOut,
} from "@/assets/icons/ds";
import { Notification } from "@/components/Interface/Notification/Notification";
import { SubscriptionCard } from "@/components/Layout/BaseLayouts/SubscriptionCard/SubscriptionCard";
import { SubscriptionActionButton } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionActionButton/SubscriptionActionButton";
import { SubscriptionProgressBar } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionStatus/SubscriptionProgressBar";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { SimCardDetail, SimCardDetailSimStatusReason } from "@/services/model";
import {
  portalTariffProductIDAtom,
  tariffCheckoutPaymentPersistenceAtom,
} from "@/utils/atoms";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import { getNextPaymentDate } from "@/utils/tariffUtils";

/**
 * "Abo-Status": whether the subscription renews, when it is billed next, and
 * why it is blocked when a payment could not be collected.
 */
export const SubscriptionStatus = ({
  simDetail,
  action,
  billingPeriodDisabledHint,
  showBillingPeriod = true,
}: {
  simDetail: SimCardDetail;
  action?: ReactNode;
  /** Same disable rules as "Ändern" / "Abo pausieren" — greyed out with tooltip. */
  billingPeriodDisabledHint?: string;
  /** Flat Home has a fixed billing period for the whole contract term. */
  showBillingPeriod?: boolean;
}) => {
  const { t, i18n } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.subscription.status",
  });
  const navigate = useNavigate();
  const setPortalTariffProductID = useSetAtom(portalTariffProductIDAtom);
  const setTariffPaymentPersistence = useSetAtom(
    tariffCheckoutPaymentPersistenceAtom,
  );

  const handleBillingPeriodSwitch = () => {
    if (!simDetail.serviceID || !simDetail.simId) {
      return;
    }
    setTariffPaymentPersistence(undefined);
    setPortalTariffProductID(simDetail.serviceID);
    navigate(
      `${portalRoutes.manageDevice.subscription.checkout}?simId=${simDetail.simId}&billingPeriodSwitch=true`,
    );
  };

  const hasLockRequested =
    simDetail.simStatusReason === SimCardDetailSimStatusReason.LOCK_REQUESTED;
  const isPending = !!simDetail.servicePending;
  // Auto-renewal is off and nothing else is going on: the subscription is
  // scheduled to stop renewing on `nextBillDate`, i.e. it is "wird pausiert".
  // `autoRenew` is optional - undefined (not yet provisioned, or a tariff
  // type that never populates it) must not be mistaken for "off".
  const isPausing =
    !hasLockRequested && !isPending && simDetail.autoRenew === false;

  // `getNextPaymentDate` counts forward from today, so without `remainingDays`
  // it lands on today and the card would promise a bill that is not due. Say
  // nothing about the date rather than the wrong thing — same guard as in
  // SubscriptionOptions.
  const nextBillDate =
    simDetail.remainingDays !== undefined
      ? getLocalizedDateString(
          getNextPaymentDate(simDetail.remainingDays),
          i18n.language,
        )
      : undefined;

  const elapsedPercentage =
    simDetail.minContractDuration && simDetail.remainingDays !== undefined
      ? ((simDetail.minContractDuration - simDetail.remainingDays) /
          simDetail.minContractDuration) *
        100
      : undefined;

  // `minContractDuration` is occasionally missing from the API for an
  // otherwise-active subscription; the billing period section only needs to
  // know monthly vs. yearly, so default to monthly (30 days) rather than
  // hiding the whole section - same `?? 30` fallback used elsewhere
  // (SubscriptionPrice, SubscriptionAutoRenewal, AddWatchSimOption,
  // ChangeWatchSimRenewal) for the same field.
  const isMonthly = (simDetail.minContractDuration ?? 30) === 30;

  return (
    <SubscriptionCard
      dataTestid="subscription-status"
      title={t("title")}
      action={action}
    >
      <div className="flex w-full flex-col gap-4 rounded-md bg-surface-quaternary p-4">
        <div className="flex w-full items-start gap-4">
          <span className="shrink-0 text-foreground-primary">
            {hasLockRequested ? (
              <GlobeCrossedOut className="size-6" />
            ) : isPausing ? (
              <RefreshCrossedOut className="size-6" />
            ) : (
              <Refresh className="size-6" />
            )}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1 text-foreground-primary">
            <p className="truncate-safe text-body-base font-semibold leading-none">
              {hasLockRequested ? t("locked") : t("active")}
            </p>
            {!hasLockRequested && (isPending || nextBillDate) && (
              <p className="truncate-safe text-body-xs leading-none">
                {isPending ? (
                  t("inPreparation")
                ) : (
                  <Trans
                    t={t}
                    i18nKey={isPausing ? "pausing" : "nextBill"}
                    components={[<span key="0" className="font-semibold" />]}
                    values={{ date: nextBillDate }}
                  />
                )}
              </p>
            )}
          </div>
        </div>
        <SubscriptionProgressBar
          percentage={hasLockRequested ? undefined : elapsedPercentage}
        />
      </div>

      {showBillingPeriod && (
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-3 rounded-md bg-surface-quaternary p-4">
            <Calendar className="size-6 shrink-0 text-foreground-primary" />
            <div className="flex min-w-0 flex-1 flex-col gap-1 leading-none text-foreground-primary">
              <p className="truncate-safe text-body-base font-semibold leading-none">
                {isMonthly
                  ? t("billingPeriod.monthly")
                  : t("billingPeriod.yearly")}
              </p>
              <p className="truncate-safe text-body-xs leading-none">
                {t("billingPeriod.label")}
              </p>
            </div>
            <SubscriptionActionButton
              dataTestid="subscription-billing-period-change"
              variant="secondary"
              label={t("billingPeriod.change")}
              disabled={!simDetail.serviceID || !simDetail.simId}
              disabledHint={billingPeriodDisabledHint}
              onClick={handleBillingPeriodSwitch}
            />
          </div>
          {isMonthly && (
            <div className="flex gap-2 text-body-sm leading-sm tracking-[0.28px] text-foreground-primary">
              <span className="inline-flex h-[1.2em] shrink-0 items-center">
                <InfoCircle className="size-4" aria-hidden />
              </span>
              <p className="min-w-0 flex-1">
                <Trans
                  t={t}
                  i18nKey="billingPeriod.yearlyHint"
                  components={[<span key="0" className="font-semibold" />]}
                />
              </p>
            </div>
          )}
        </div>
      )}

      {hasLockRequested && (
        <Notification
          dataTestid="subscription-payment-failed"
          tone="error"
          title={t("paymentFailed.title")}
          description={
            <Trans
              t={t}
              i18nKey="paymentFailed.text"
              components={[
                <Link
                  key="0"
                  className="underline"
                  replace
                  to={portalRoutes.documents.payments}
                />,
              ]}
            />
          }
        />
      )}
    </SubscriptionCard>
  );
};
